import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  TextField,
  Box,
  Avatar,
  IconButton,
  Typography,
  FormControlLabel,
  Switch,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { UserProfile, ProfileFormData, UserPreferences, NotificationPreferences, PrivacySettings, BusinessInfo, ContactDetails } from '../types/profileTypes';
import { useProfile } from '../hooks/useProfile';
import { useAuth } from '../../../hooks/useAuth';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import { SPACING_TOKENS } from '../../../design-system/tokens/spacing';
import { Card, CardBody } from '../../../design-system/components';

interface EditProfileFormProps {
  profile: UserProfile | null;
  onCancel?: () => void;
  onSave?: () => void;
}

interface EditProfileFormData {
  username: string;
  email: string;
  phone: string;
  location: string;
  company_name: string;
  business_type: string;
  website: string;
  language: string;
  timezone: string;
  profile_visibility: 'public' | 'private' | 'organization_only';
  email_notifications: boolean;
  push_notifications: boolean;
  sms_notifications: boolean;
  marketing_emails: boolean;
  show_contact_details: boolean;
  show_business_info: boolean;
  show_farm_associations: boolean;
}

const languages = [
  { code: 'en', name: 'English' },
  { code: 'pt', name: 'Português' },
  { code: 'es', name: 'Español' },
];

const timezones = [
  'America/Sao_Paulo',
  'America/New_York',
  'Europe/London',
  'Europe/Madrid',
  'UTC',
];

export const EditProfileForm: React.FC<EditProfileFormProps> = ({
  profile,
  onCancel,
  onSave
}) => {
  const { t } = useTranslation();
  const { createProfile, updateProfile, loading, error } = useProfile();
  const { authState } = useAuth();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState<EditProfileFormData>({
    username: profile?.username || '',
    email: profile?.contact_details?.email || '',
    phone: profile?.contact_details?.phone || '',
    location: profile?.contact_details?.address || '',
    company_name: profile?.business_info?.company_name || '',
    business_type: profile?.business_info?.business_type || '',
    website: profile?.business_info?.website || '',
    language: profile?.preferences?.language || 'en',
    timezone: profile?.preferences?.timezone || 'UTC',
    profile_visibility: profile?.preferences?.privacy?.profile_visibility || 'public',
    email_notifications: profile?.preferences?.notifications?.email_notifications || true,
    push_notifications: profile?.preferences?.notifications?.push_notifications || true,
    sms_notifications: profile?.preferences?.notifications?.sms_notifications || false,
    marketing_emails: profile?.preferences?.notifications?.marketing_emails || false,
    show_contact_details: profile?.preferences?.privacy?.show_contact_details || true,
    show_business_info: profile?.preferences?.privacy?.show_business_info || true,
    show_farm_associations: profile?.preferences?.privacy?.show_farm_associations || true,
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        username: profile.username || '',
        email: profile.contact_details?.email || '',
        phone: profile.contact_details?.phone || '',
        location: profile.contact_details?.address || '',
        company_name: profile.business_info?.company_name || '',
        business_type: profile.business_info?.business_type || '',
        website: profile.business_info?.website || '',
        language: profile.preferences?.language || 'en',
        timezone: profile.preferences?.timezone || 'UTC',
        profile_visibility: profile.preferences?.privacy?.profile_visibility || 'public',
        email_notifications: profile.preferences?.notifications?.email_notifications || true,
        push_notifications: profile.preferences?.notifications?.push_notifications || true,
        sms_notifications: profile.preferences?.notifications?.sms_notifications || false,
        marketing_emails: profile.preferences?.notifications?.marketing_emails || false,
        show_contact_details: profile.preferences?.privacy?.show_contact_details || true,
        show_business_info: profile.preferences?.privacy?.show_business_info || true,
        show_farm_associations: profile.preferences?.privacy?.show_farm_associations || true,
      });
    }
  }, [profile]);

  const handleInputChange = (field: keyof EditProfileFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSelectChange = (field: keyof EditProfileFormData) => (
    event: SelectChangeEvent<string>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSwitchChange = (field: keyof EditProfileFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    try {
      const profileData = {
        username: formData.username,
        contact_details: {
          email: formData.email,
          phone: formData.phone,
          address: formData.location,
        } as ContactDetails,
        business_info: {
          company_name: formData.company_name,
          business_type: formData.business_type,
          website: formData.website,
        } as BusinessInfo,
        preferences: {
          language: formData.language,
          timezone: formData.timezone,
          notifications: {
            email_notifications: formData.email_notifications,
            push_notifications: formData.push_notifications,
            sms_notifications: formData.sms_notifications,
            marketing_emails: formData.marketing_emails,
          },
          privacy: {
            profile_visibility: formData.profile_visibility,
            show_contact_details: formData.show_contact_details,
            show_business_info: formData.show_business_info,
            show_farm_associations: formData.show_farm_associations,
          },
        } as UserPreferences,
      };

      if (profile) {
        await updateProfile(profileData);
      } else {
        await createProfile(profileData);
      }
      
      setShowSuccess(true);
      onSave?.();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'An error occurred');
      setShowError(true);
    }
  };

  const handleCancel = () => {
    setFormData({
      username: profile?.username || '',
      email: profile?.contact_details?.email || '',
      phone: profile?.contact_details?.phone || '',
      location: profile?.contact_details?.address || '',
      company_name: profile?.business_info?.company_name || '',
      business_type: profile?.business_info?.business_type || '',
      website: profile?.business_info?.website || '',
      language: profile?.preferences?.language || 'en',
      timezone: profile?.preferences?.timezone || 'UTC',
      profile_visibility: profile?.preferences?.privacy?.profile_visibility || 'public',
       email_notifications: profile?.preferences?.notifications?.email_notifications || true,
       push_notifications: profile?.preferences?.notifications?.push_notifications || true,
       sms_notifications: profile?.preferences?.notifications?.sms_notifications || false,
       marketing_emails: profile?.preferences?.notifications?.marketing_emails || false,
       show_contact_details: profile?.preferences?.privacy?.show_contact_details || true,
       show_business_info: profile?.preferences?.privacy?.show_business_info || true,
       show_farm_associations: profile?.preferences?.privacy?.show_farm_associations || true,
    });
    setImageFile(null);
    setImagePreview(null);
    onCancel?.();
  };

  if (!authState.user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Typography>Please log in to edit your profile.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', minHeight: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Action Bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          borderBottom: `1px solid ${DESIGN_TOKENS.colors.primary[100]}`,
          backgroundColor: DESIGN_TOKENS.colors.background.paper,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.text.primary }}>
          Edit Profile
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            sx={{
              borderColor: DESIGN_TOKENS.colors.primary[300],
              color: DESIGN_TOKENS.colors.primary[600],
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              backgroundColor: DESIGN_TOKENS.colors.primary[500],
              '&:hover': {
                backgroundColor: DESIGN_TOKENS.colors.primary[600],
              },
            }}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          backgroundColor: DESIGN_TOKENS.colors.background.default,
          px: { xs: 2, md: 4 },
          py: { xs: 3, md: 4 },
          pb: { xs: 10, md: 12 },
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: DESIGN_TOKENS.colors.background.paper,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: DESIGN_TOKENS.colors.primary[300],
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: DESIGN_TOKENS.colors.primary[400],
            },
          },
        }}
      >
        {/* Main Content Layout */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* First Row */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'stretch' }}>
            {/* Profile Photo Section */}
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 16px)', lg: '1 1 calc(33.333% - 16px)' }, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <Card 
                elevation={2}
                sx={{ 
                  height: '100%',
                  backgroundColor: DESIGN_TOKENS.colors.background.paper,
                  border: `1px solid ${DESIGN_TOKENS.colors.primary[100]}`,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <CardBody sx={{ p: 4 }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 600,
                      color: DESIGN_TOKENS.colors.text.primary,
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <PhotoCameraIcon sx={{ color: DESIGN_TOKENS.colors.primary[500] }} />
                    Profile Photo
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, alignItems: 'center' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                          <Avatar
                            src={imagePreview || profile?.contact_details?.avatar_url}
                            sx={{
                              width: 120,
                              height: 120,
                              fontSize: '2rem',
                              backgroundColor: DESIGN_TOKENS.colors.primary[100],
                              color: DESIGN_TOKENS.colors.primary[700],
                              border: `3px solid ${DESIGN_TOKENS.colors.primary[200]}`
                            }}
                          >
                            {formData.username?.charAt(0)?.toUpperCase()}
                          </Avatar>
                          <IconButton
                            component="label"
                            sx={{
                              position: 'absolute',
                              bottom: -8,
                              right: -8,
                              backgroundColor: DESIGN_TOKENS.colors.primary[500],
                              color: 'white',
                              width: 40,
                              height: 40,
                              '&:hover': {
                                backgroundColor: DESIGN_TOKENS.colors.primary[600]
                              }
                            }}
                          >
                            <PhotoCameraIcon sx={{ fontSize: 20 }} />
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={handleImageChange}
                            />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: DESIGN_TOKENS.colors.text.primary,
                            mb: 1,
                            fontWeight: 500
                          }}
                        >
                          Click to change your profile picture
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: DESIGN_TOKENS.colors.text.secondary
                          }}
                        >
                          Recommended: Square image, at least 400x400 pixels
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardBody>
              </Card>
            </Box>

            {/* Personal Information Section */}
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 16px)', lg: '1 1 calc(66.667% - 16px)' }, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <Card 
                elevation={2}
                sx={{ 
                  height: '100%',
                  backgroundColor: DESIGN_TOKENS.colors.background.paper,
                  border: `1px solid ${DESIGN_TOKENS.colors.primary[100]}`,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <CardBody sx={{ p: 4 }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 600,
                      color: DESIGN_TOKENS.colors.text.primary,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <PersonIcon sx={{ color: DESIGN_TOKENS.colors.primary[500] }} />
                    Personal Information
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: DESIGN_TOKENS.colors.text.secondary,
                      mb: 3
                    }}
                  >
                    Update your basic personal details and contact information
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Username"
                        value={formData.username}
                        onChange={handleInputChange('username')}
                        variant="outlined"
                      />
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange('email')}
                        variant="outlined"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={formData.phone}
                        onChange={handleInputChange('phone')}
                        variant="outlined"
                      />
                      <TextField
                        fullWidth
                        label="Location"
                        value={formData.location}
                        onChange={handleInputChange('location')}
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </CardBody>
              </Card>
            </Box>
          </Box>

          {/* Second Row */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'stretch' }}>
            {/* Business Information Section */}
            <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 calc(50% - 16px)' }, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <Card 
                elevation={2}
                sx={{ 
                  height: '100%',
                  backgroundColor: DESIGN_TOKENS.colors.background.paper,
                  border: `1px solid ${DESIGN_TOKENS.colors.primary[100]}`,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <CardBody sx={{ p: 4 }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 600,
                      color: DESIGN_TOKENS.colors.text.primary,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <BusinessIcon sx={{ color: DESIGN_TOKENS.colors.primary[500] }} />
                    Business Information
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: DESIGN_TOKENS.colors.text.secondary,
                      mb: 3
                    }}
                  >
                    Manage your professional and company details
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Company Name"
                      value={formData.company_name}
                      onChange={handleInputChange('company_name')}
                      variant="outlined"
                    />
                    <TextField
                      fullWidth
                      label="Business Type"
                      value={formData.business_type}
                      onChange={handleInputChange('business_type')}
                      variant="outlined"
                    />
                    <TextField
                      fullWidth
                      label="Website"
                      value={formData.website}
                      onChange={handleInputChange('website')}
                      variant="outlined"
                    />
                  </Box>
                </CardBody>
              </Card>
            </Box>

            {/* Preferences Section */}
            <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 calc(50% - 16px)' }, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <Card 
                elevation={2}
                sx={{ 
                  height: '100%',
                  backgroundColor: DESIGN_TOKENS.colors.background.paper,
                  border: `1px solid ${DESIGN_TOKENS.colors.primary[100]}`,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <CardBody sx={{ p: 4 }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 600,
                      color: DESIGN_TOKENS.colors.text.primary,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <SettingsIcon sx={{ color: DESIGN_TOKENS.colors.primary[500] }} />
                    Preferences
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: DESIGN_TOKENS.colors.text.secondary,
                      mb: 3
                    }}
                  >
                    Customize your language and timezone settings
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Language</InputLabel>
                      <Select
                        value={formData.language}
                        label="Language"
                        onChange={handleSelectChange('language')}
                      >
                        {languages.map((lang) => (
                          <MenuItem key={lang.code} value={lang.code}>
                            {lang.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel>Timezone</InputLabel>
                      <Select
                        value={formData.timezone}
                        label="Timezone"
                        onChange={handleSelectChange('timezone')}
                      >
                        {timezones.map((tz) => (
                          <MenuItem key={tz} value={tz}>
                            {tz}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </CardBody>
              </Card>
            </Box>
          </Box>

          {/* Third Row */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'stretch' }}>
            {/* Privacy Settings Section */}
            <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 calc(50% - 16px)' }, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <Card 
                elevation={2}
                sx={{ 
                  height: '100%',
                  backgroundColor: DESIGN_TOKENS.colors.background.paper,
                  border: `1px solid ${DESIGN_TOKENS.colors.primary[100]}`,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <CardBody sx={{ p: 4 }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 600,
                      color: DESIGN_TOKENS.colors.text.primary,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <SecurityIcon sx={{ color: DESIGN_TOKENS.colors.primary[500] }} />
                    Privacy Settings
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: DESIGN_TOKENS.colors.text.secondary,
                      mb: 3
                    }}
                  >
                    Control who can see your information and how it's shared
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Profile Visibility</InputLabel>
                      <Select
                        value={formData.profile_visibility}
                        label="Profile Visibility"
                        onChange={handleSelectChange('profile_visibility')}
                      >
                        <MenuItem value="public">Public</MenuItem>
                        <MenuItem value="private">Private</MenuItem>
                        <MenuItem value="organization_only">Organization Only</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.show_contact_details}
                          onChange={handleSwitchChange('show_contact_details')}
                        />
                      }
                      label="Show Contact Details"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.show_business_info}
                          onChange={handleSwitchChange('show_business_info')}
                        />
                      }
                      label="Show Business Information"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.show_farm_associations}
                          onChange={handleSwitchChange('show_farm_associations')}
                        />
                      }
                      label="Show Farm Associations"
                    />
                  </Box>
                </CardBody>
              </Card>
            </Box>

            {/* Notification Settings Section */}
            <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 calc(50% - 16px)' }, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <Card 
                elevation={2}
                sx={{ 
                  height: '100%',
                  backgroundColor: DESIGN_TOKENS.colors.background.paper,
                  border: `1px solid ${DESIGN_TOKENS.colors.primary[100]}`,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <CardBody sx={{ p: 4 }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 600,
                      color: DESIGN_TOKENS.colors.text.primary,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <NotificationsIcon sx={{ color: DESIGN_TOKENS.colors.primary[500] }} />
                    Notification Settings
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: DESIGN_TOKENS.colors.text.secondary,
                      mb: 3
                    }}
                  >
                    Choose how you want to receive notifications and updates
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.email_notifications}
                          onChange={handleSwitchChange('email_notifications')}
                        />
                      }
                      label="Email Notifications"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.push_notifications}
                          onChange={handleSwitchChange('push_notifications')}
                        />
                      }
                      label="Push Notifications"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.sms_notifications}
                          onChange={handleSwitchChange('sms_notifications')}
                        />
                      }
                      label="SMS Notifications"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.marketing_emails}
                          onChange={handleSwitchChange('marketing_emails')}
                        />
                      }
                      label="Marketing Emails"
                    />
                  </Box>
                </CardBody>
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Success/Error Snackbars */}
      {showSuccess && (
        <Alert 
          severity="success" 
          onClose={() => setShowSuccess(false)}
          sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
        >
          Profile updated successfully!
        </Alert>
      )}
      
      {showError && (
        <Alert 
          severity="error" 
          onClose={() => setShowError(false)}
          sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
        >
          {errorMessage || 'An error occurred while updating your profile.'}
        </Alert>
      )}
    </Box>
  );
};

export default EditProfileForm;